// =============================================================================
// src/lib/judicial-format.ts
//
// Pretty-printers for federal-judicial-district result rows. Shared by
// HeroMap (homepage) and ExplorePage (Explore) so both surfaces format
// circuit + district names identically.
// =============================================================================

const ORDINAL_CIRCUITS: Record<string, string> = {
  '1':  '1st',
  '2':  '2nd',
  '3':  '3rd',
  '4':  '4th',
  '5':  '5th',
  '6':  '6th',
  '7':  '7th',
  '8':  '8th',
  '9':  '9th',
  '10': '10th',
  '11': '11th',
  '12': '12th',
  'DC': 'D.C.',
}

/** Turn the raw circuit identifier into a display string (e.g. '9' → '9th Circuit'). */
export function formatCircuit(circuit: string | undefined | null): string | null {
  if (!circuit) return null
  return `${ORDINAL_CIRCUITS[circuit] ?? `${circuit}th`} Circuit`
}

/**
 * Compose the short district name from the identifiers stored on a
 * federal_judicial_district boundary. Examples:
 *   { district_name: 'Arizona', state_name: 'Arizona' }   → 'District of Arizona'
 *   { district_name: 'Northern', state_name: 'California' } → 'Northern District of California'
 *   { district_name: 'District of Columbia', state_name: 'District of Columbia' }
 *                                                          → 'District of Columbia'
 */
export function formatJudicialDistrict(identifiers: Record<string, unknown>): string {
  const districtName = identifiers.district_name as string | undefined
  const stateName = identifiers.state_name as string | undefined
  if (!districtName || !stateName) return (identifiers.name as string) ?? 'District'
  if (districtName === stateName) {
    // D.C. edge case: stateName is already "District of Columbia", so we'd
    // produce "District of District of Columbia" if we naively prefix.
    // Skip the prefix when the state name already begins with "District of".
    if (/^District of/i.test(stateName)) return stateName
    return `District of ${stateName}`
  }
  return `${districtName} District of ${stateName}`
}
